import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Menu, Smartphone, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { appMeta } from '@/config/iphonefastflip';

const navLinks = [
  { href: '/', label: 'Strona główna', route: true },
  { href: '/features', label: 'Funkcje', route: true },
  { href: '/#jak-dziala', label: 'Jak działa', route: false },
  { href: '/#plany', label: 'Plany', route: false },
  { href: '/support', label: 'Pomoc', route: true },
] as const;

const Navbar: React.FC = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  return (
    <>
      <motion.header
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors ${
          scrolled ? 'border-white/[0.08] bg-[#080b12]/90 backdrop-blur-xl' : 'border-transparent bg-transparent'
        }`}
      >
        <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3" aria-label="Alterolis — strona główna">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 shadow-[0_8px_30px_rgba(59,130,246,0.25)]">
              <Smartphone className="h-5 w-5 text-white" />
            </div>
            <div className="leading-none">
              <p className="text-sm font-black tracking-tight text-white">Alterolis</p>
              <p className="mt-1 text-[10px] font-semibold text-slate-500">iPhoneFastFlip</p>
            </div>
          </Link>

          <div className="hidden items-center gap-7 lg:flex">
            {navLinks.map((link) =>
              link.route ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link text-sm font-semibold ${location.pathname === link.href ? 'active text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a key={link.href} href={link.href} className="nav-link text-sm font-semibold text-slate-400 hover:text-white">
                  {link.label}
                </a>
              ),
            )}
          </div>

          <div className="hidden lg:block">
            <a href={appMeta.webUrl} className="button-primary !min-h-10 !px-4 !py-2 text-sm">
              Uruchom aplikację <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>

          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="grid h-11 w-11 place-items-center rounded-xl border border-white/[0.08] bg-white/[0.035] text-white lg:hidden"
            aria-label={open ? 'Zamknij menu' : 'Otwórz menu'}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed inset-x-4 top-20 z-50 rounded-2xl border border-white/10 bg-[#0d121c]/95 p-4 shadow-2xl backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((link) =>
                link.route ? (
                  <Link key={link.href} to={link.href} className="rounded-xl px-4 py-3 font-semibold text-slate-300 hover:bg-white/5 hover:text-white">
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.href} href={link.href} className="rounded-xl px-4 py-3 font-semibold text-slate-300 hover:bg-white/5 hover:text-white">
                    {link.label}
                  </a>
                ),
              )}
              <a href={appMeta.webUrl} className="button-primary mt-3 w-full">
                Uruchom aplikację <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
